import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Send } from "lucide-react";
import { TEST_LIST, PACKAGES } from "../data/siteConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#1E88E5] focus:outline-none focus:ring-2 focus:ring-[#1E88E5]/30";

const testOptions = [...TEST_LIST.map((t) => t.name), ...PACKAGES.map((p) => p.name), "Other / Not sure"];

const EnquiryForm = ({ mode = "booking", enquiryType = "booking", defaultTest = "", defaultPreference = "Collection Center Visit", testIdPrefix = "enquiry" }) => {
  const isContact = mode === "contact";
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    test: defaultTest,
    collection_preference: defaultPreference,
    preferred_date: "",
    preferred_time: "",
    subject: "",
    message: "",
    consent: false,
    website: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      const payload = { ...form, enquiry_type: enquiryType };
      if (isContact) delete payload.collection_preference;
      const { data } = await axios.post(`${API}/enquiries`, payload);
      setStatus({ state: "success", message: data.message });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" ")
        : typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or call us directly.";
      setStatus({ state: "error", message: msg });
    }
  };

  if (status.state === "success") {
    return (
      <div
        className="rounded-2xl border border-green-200 bg-green-50 p-8 text-center"
        data-testid={`${testIdPrefix}-success-message`}
        role="status"
      >
        <CheckCircle2 size={44} className="mx-auto text-green-600" aria-hidden="true" />
        <h3 className="mt-4 font-heading text-xl font-bold text-[#0B3056]">Enquiry Received</h3>
        <p className="mt-2 text-sm text-slate-600">{status.message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid={`${testIdPrefix}-form`} noValidate={false}>
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${testIdPrefix}-name`} className="mb-1.5 block text-sm font-medium text-slate-700">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input id={`${testIdPrefix}-name`} data-testid={`${testIdPrefix}-name-input`} required minLength={2} maxLength={100} value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
        </div>
        <div>
          <label htmlFor={`${testIdPrefix}-mobile`} className="mb-1.5 block text-sm font-medium text-slate-700">
            Mobile Number <span className="text-red-500">*</span>
          </label>
          <input id={`${testIdPrefix}-mobile`} data-testid={`${testIdPrefix}-mobile-input`} required type="tel" inputMode="numeric" value={form.mobile} onChange={set("mobile")} className={inputCls} placeholder="10-digit mobile number" />
        </div>
      </div>

      <div>
        <label htmlFor={`${testIdPrefix}-email`} className="mb-1.5 block text-sm font-medium text-slate-700">
          Email Address
        </label>
        <input id={`${testIdPrefix}-email`} data-testid={`${testIdPrefix}-email-input`} type="email" maxLength={150} value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
      </div>

      {isContact ? (
        <div>
          <label htmlFor={`${testIdPrefix}-subject`} className="mb-1.5 block text-sm font-medium text-slate-700">
            Subject
          </label>
          <input id={`${testIdPrefix}-subject`} data-testid={`${testIdPrefix}-subject-input`} maxLength={150} value={form.subject} onChange={set("subject")} className={inputCls} placeholder="What is your enquiry about?" />
        </div>
      ) : (
        <>
          <div>
            <label htmlFor={`${testIdPrefix}-test`} className="mb-1.5 block text-sm font-medium text-slate-700">
              Test / Package Required
            </label>
            <select id={`${testIdPrefix}-test`} data-testid={`${testIdPrefix}-test-select`} value={form.test} onChange={set("test")} className={inputCls}>
              <option value="">Select a test or package</option>
              {testOptions.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium text-slate-700">Collection Preference</legend>
            <div className="flex flex-wrap gap-3">
              {["Collection Center Visit", "Home Sample Collection"].map((opt) => (
                <label key={opt} className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${form.collection_preference === opt ? "border-[#1E88E5] bg-[#F0F7FA] text-[#0B3056]" : "border-gray-300 text-slate-600"}`}>
                  <input
                    type="radio"
                    name={`${testIdPrefix}-preference`}
                    data-testid={`${testIdPrefix}-preference-${opt === "Collection Center Visit" ? "center" : "home"}`}
                    value={opt}
                    checked={form.collection_preference === opt}
                    onChange={set("collection_preference")}
                    className="accent-[#1E88E5]"
                  />
                  {opt}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor={`${testIdPrefix}-date`} className="mb-1.5 block text-sm font-medium text-slate-700">
                Preferred Date
              </label>
              <input id={`${testIdPrefix}-date`} data-testid={`${testIdPrefix}-date-input`} type="date" min={new Date().toISOString().split("T")[0]} value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
            </div>
            <div>
              <label htmlFor={`${testIdPrefix}-time`} className="mb-1.5 block text-sm font-medium text-slate-700">
                Preferred Time
              </label>
              <input id={`${testIdPrefix}-time`} data-testid={`${testIdPrefix}-time-input`} type="time" value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} />
            </div>
          </div>
        </>
      )}

      <div>
        <label htmlFor={`${testIdPrefix}-message`} className="mb-1.5 block text-sm font-medium text-slate-700">
          Message
        </label>
        <textarea id={`${testIdPrefix}-message`} data-testid={`${testIdPrefix}-message-input`} rows={3} maxLength={1000} value={form.message} onChange={set("message")} className={inputCls} placeholder="Any additional details (optional)" />
      </div>

      <label className="flex cursor-pointer items-start gap-2.5 text-sm text-slate-600">
        <input
          type="checkbox"
          required
          checked={form.consent}
          onChange={set("consent")}
          data-testid={`${testIdPrefix}-consent-checkbox`}
          className="mt-1 h-4 w-4 accent-[#1E88E5]"
        />
        I agree to be contacted regarding my enquiry or appointment.
      </label>

      {status.state === "error" && (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid={`${testIdPrefix}-error-message`}>
          {status.message}
        </p>
      )}

      <button
        type="submit"
        disabled={status.state === "loading"}
        data-testid={`${testIdPrefix}-submit-button`}
        className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-[#1E88E5] px-6 py-3 text-sm font-semibold text-white shadow-sm transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#1565C0] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#1E88E5] focus:ring-offset-2 disabled:opacity-60 sm:w-auto"
      >
        <Send size={17} aria-hidden="true" />
        {status.state === "loading" ? "Submitting..." : "Submit Enquiry"}
      </button>

      <p className="text-xs leading-relaxed text-slate-500">
        Please do not submit medical reports, prescriptions, or other sensitive health information through this form
        unless specifically requested through a secure channel.
      </p>
    </form>
  );
};

export default EnquiryForm;
